import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import toTitleCase from 'to-title-case';
import striptags from 'striptags';
import config from '../utils/config';

const getSchemaOrgJSONLD = ({
  isBlogPost,
  isArticle,
  isTheme,
  url,
  title,
  image,
  description,
  datePublished,
  dateModified,
  authorName,
  price,
}) => {
  const schemaOrgJSONLD = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url,
      name: title,
      alternateName: config.siteTitle,
    },
  ];

  if (isBlogPost || isArticle) {
    return [
      ...schemaOrgJSONLD,
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': url,
              name: title,
              image,
            },
          },
        ],
      },
      {
        '@context': 'http://schema.org',
        '@type': 'BlogPosting',
        url,
        name: title,
        alternateName: config.siteTitle,
        headline: title,
        image: {
          '@type': 'ImageObject',
          url: image,
        },
        description,
        author: {
          '@type': 'Person',
          name: authorName || 'Sergio Chiavassa',
        },
        publisher: {
          '@type': 'Organization',
          url: 'https://pixelmoxie.net',
          name: 'PixelMoxie',
          logo: {
            '@type': 'ImageObject',
            name: 'PixelMoxie Logo',
            width: '496',
            height: '60',
            url: config.siteLogo,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebSite',
          '@id': config.siteUrl,
        },
        datePublished,
        ...(dateModified && { dateModified }),
      },
    ];
  } else if (isTheme) {
    return [
      ...schemaOrgJSONLD,
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': url,
              name: title,
              image,
            },
          },
        ],
      },
      {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: title,
        description,
        url,
        image: {
          '@type': 'ImageObject',
          url: image,
        },
        offers: {
          '@type': 'Offer',
          price,
          priceCurrency: 'USD',
        },
        manufacturer: {
          '@type': 'Organization',
          url: 'https://pixelmoxie.net',
          name: 'PixelMoxie',
          logo: {
            '@type': 'ImageObject',
            name: 'PixelMoxie Logo',
            width: '496',
            height: '60',
            url: config.siteLogo,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebSite',
          '@id': config.siteUrl,
        },
      },
    ];
  }
  return schemaOrgJSONLD;
};

const SEO = ({ postData, postImage, pathPrefix, isBlogPost, isArticle, isTheme }) => {
  const postMeta = postData.frontmatter || {};
  const postSEO = postMeta.seo || {};
  const fields = postData.fields || {};
  const title = postSEO.title || fields.title || config.siteTitle;
  const description =
    postSEO.description || postMeta.excerpt || postData.excerpt || config.siteDescription;
  const image = postImage ? `${config.siteUrl}${postImage}` : config.siteImage;
  const url = fields.slug ? `${config.siteUrl}${pathPrefix}${fields.slug}` : config.siteUrl;
  const { datePublished } = fields;
  const dateModified = postMeta.dateModified || postMeta.modified;
  const authorName = postMeta.author && postMeta.author.name;
  const isOGArticle = isBlogPost || isArticle || isTheme;

  const schemaOrgJSONLD = getSchemaOrgJSONLD({
    isBlogPost,
    isArticle,
    isTheme,
    url,
    title,
    image,
    description,
    datePublished,
    dateModified,
    authorName,
    price: postMeta.price,
  });

  return (
    <Helmet>
      {/* General tags */}
      <title>{toTitleCase(title)}</title>
      <meta name="description" content={striptags(description)} />
      <meta name="image" content={image} />

      {/* Schema.org tags */}
      <script type="application/ld+json">{JSON.stringify(schemaOrgJSONLD)}</script>

      {/* OpenGraph tags */}
      <meta property="og:url" content={url} />
      {isOGArticle && <meta property="og:type" content="article" />}
      {isOGArticle &&
        datePublished && <meta property="article:published_time" content={datePublished} />}
      {isOGArticle &&
        dateModified && <meta property="article:modified_time" content={dateModified} />}
      {isOGArticle && dateModified && <meta property="og:updated_time" content={dateModified} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="fb:app_id" content={config.fbAppID} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={config.twitter} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

SEO.propTypes = {
  postData: PropTypes.shape({
    frontmatter: PropTypes.any,
    excerpt: PropTypes.any,
  }).isRequired,
  postImage: PropTypes.string,
  pathPrefix: PropTypes.string,
  isBlogPost: PropTypes.bool,
  isArticle: PropTypes.bool,
  isTheme: PropTypes.bool,
};

SEO.defaultProps = {
  isBlogPost: false,
  isArticle: false,
  isTheme: false,
  postImage: null,
  pathPrefix: '',
};

export default SEO;



// WEBPACK FOOTER //
// ./src/components/seo.js